import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import {
  getAccountOrganisations,
  getAvailableUsergroups,
  addAccountOrganisation,
  removeAccountOrganisation,
  allocateUsergroup,
  deallocateUsergroup,
  searchOrganisations,
} from '../api';
import type { AccountOrganisation, AvailableUsergroup, AvailableUsergroupsOrg } from '../types';

interface AllocationCardProps {
  accountId: string;
}

// =============================================================================
// OrgColumn — one column per linked organisation
// =============================================================================

interface OrgColumnProps {
  accountId: string;
  org: AccountOrganisation;
  usergroups: AvailableUsergroup[];
  onOrgRemoved: () => void;
}

interface PendingMove {
  ug: AvailableUsergroup;
}

function OrgColumn({ accountId, org, usergroups, onOrgRemoved }: OrgColumnProps) {
  const queryClient = useQueryClient();
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'available-usergroups'] });
    queryClient.invalidateQueries({ queryKey: ['account-sensors', accountId] });
  };

  // Fix 1: mutation lives inside OrgColumn so each column's remove button gates
  // independently — no shared isPending that disables unrelated columns.
  const removeOrgMutation = useMutation({
    mutationFn: () => removeAccountOrganisation(accountId, org.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'organisations'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'available-usergroups'] });
      toast.success('Organisation removed');
      onOrgRemoved();
    },
    onError: () => toast.error('Failed to remove organisation'),
  });

  const allocateMutation = useMutation({
    mutationFn: (ugId: string) => allocateUsergroup(accountId, ugId),
    onSuccess: () => {
      invalidate();
      toast.success('Usergroup allocated');
    },
    onError: () => toast.error('Failed to allocate usergroup'),
  });

  const deallocateMutation = useMutation({
    mutationFn: (ugId: string) => deallocateUsergroup(accountId, ugId),
    onSuccess: () => {
      invalidate();
      toast.success('Usergroup released');
    },
    onError: () => toast.error('Failed to release usergroup'),
  });

  const handleCheck = (ug: AvailableUsergroup, checked: boolean) => {
    if (checked) {
      if (ug.ownerAccount && !ug.ownedByThisAccount) {
        // Requires move confirmation
        setPendingMove({ ug });
      } else {
        allocateMutation.mutate(ug.id);
      }
    } else {
      deallocateMutation.mutate(ug.id);
    }
  };

  const isPending = allocateMutation.isPending || deallocateMutation.isPending;

  return (
    <div className="flex flex-col gap-2 min-w-[200px] flex-1">
      <div className="flex items-center justify-between gap-2 border-b pb-2">
        <span className="text-sm font-medium truncate" title={org.name}>{org.name}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={() => removeOrgMutation.mutate()}
          disabled={removeOrgMutation.isPending}
          aria-label={`Remove ${org.name}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {usergroups.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No usergroups in this organisation.</p>
      ) : (
        <div className="space-y-1">
          {usergroups.map((ug) => (
            // Fix 2: use htmlFor on <label> + id on Checkbox for correct association;
            // no redundant aria-label on the Checkbox input itself.
            <label
              key={ug.id}
              htmlFor={`ug-${ug.id}`}
              className="flex items-center gap-2 rounded px-1 py-1 text-sm cursor-pointer hover:bg-muted/50"
            >
              <Checkbox
                id={`ug-${ug.id}`}
                checked={ug.ownedByThisAccount}
                disabled={isPending || ug.archived}
                onCheckedChange={(checked) => handleCheck(ug, checked === true)}
              />
              <span className={ug.archived ? 'text-muted-foreground line-through' : ''}>
                {ug.name}
              </span>
              {ug.sensorCount > 0 && (
                <Badge variant="secondary" className="text-xs ml-auto shrink-0">
                  {ug.sensorCount}
                </Badge>
              )}
              {ug.ownerAccount && !ug.ownedByThisAccount && (
                <Badge variant="outline" className="text-xs shrink-0">
                  {ug.ownerAccount.name}
                </Badge>
              )}
            </label>
          ))}
        </div>
      )}

      {/* Move confirmation dialog */}
      <AlertDialog open={!!pendingMove} onOpenChange={(open) => { if (!open) setPendingMove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move Usergroup</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingMove && (
                <>
                  <strong>{pendingMove.ug.name}</strong> is currently owned by{' '}
                  <strong>{pendingMove.ug.ownerAccount?.name}</strong>. Moving it will
                  reassign ownership to this account. Continue?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingMove) {
                  allocateMutation.mutate(pendingMove.ug.id);
                  setPendingMove(null);
                }
              }}
            >
              Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =============================================================================
// Add Organisation Popover
// =============================================================================

function AddOrgPopover({ onAdd }: { onAdd: (orgId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: orgs, isLoading } = useQuery({
    queryKey: ['organisations-search', 'customer', debouncedSearch],
    queryFn: () => searchOrganisations(debouncedSearch, { type: 'customer' }),
    enabled: open,
  });

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add organisation
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b">
          {/* Fix 3: accessible label for screen readers */}
          <input
            aria-label="Search organisations"
            autoFocus
            placeholder="Search organisations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-56 overflow-y-auto p-1">
          {isLoading ? (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center">Searching...</div>
          ) : orgs && orgs.length > 0 ? (
            orgs.map((org) => (
              <button
                key={org.id}
                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer truncate"
                onClick={() => { onAdd(org.id); setOpen(false); setSearch(''); }}
              >
                {org.name}
              </button>
            ))
          ) : (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
              {debouncedSearch ? 'No matching organisations' : 'Type to search...'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// AllocationCard
// =============================================================================

export default function AllocationCard({ accountId }: AllocationCardProps) {
  const queryClient = useQueryClient();

  const {
    data: orgs,
    isLoading: orgsLoading,
    isError: orgsError,
    refetch: refetchOrgs,
  } = useQuery({
    queryKey: ['accounts', accountId, 'organisations'],
    queryFn: () => getAccountOrganisations(accountId),
  });

  const {
    data: availableOrgs,
    isLoading: ugLoading,
    isError: ugError,
    refetch: refetchUg,
  } = useQuery({
    queryKey: ['accounts', accountId, 'available-usergroups'],
    queryFn: () => getAvailableUsergroups(accountId),
  });

  const addOrgMutation = useMutation({
    mutationFn: (organisationId: string) => addAccountOrganisation(accountId, organisationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'organisations'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'available-usergroups'] });
      toast.success('Organisation added');
    },
    onError: () => toast.error('Failed to add organisation'),
  });

  const isLoading = orgsLoading || ugLoading;
  const isError = orgsError || ugError;

  // Build a lookup from org id → available usergroups
  const ugByOrgId = (availableOrgs ?? []).reduce<Record<string, AvailableUsergroupsOrg>>(
    (acc, o) => { acc[o.id] = o; return acc; },
    {}
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">Organisation &amp; Usergroup Allocation</CardTitle>
        {!isLoading && !isError && (
          <AddOrgPopover onAdd={(id) => addOrgMutation.mutate(id)} />
        )}
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div data-testid="allocation-skeleton" className="flex gap-4">
            <Skeleton className="h-40 flex-1" />
            <Skeleton className="h-40 flex-1" />
          </div>
        )}

        {isError && !isLoading && (
          <div className="text-center py-6 space-y-2">
            <p className="text-sm text-destructive">Failed to load allocation data.</p>
            <Button variant="outline" size="sm" onClick={() => { void refetchOrgs(); void refetchUg(); }}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {(!orgs || orgs.length === 0) ? (
              <p className="text-sm text-muted-foreground">
                No organisations linked. Use "Add organisation" to begin allocating usergroups.
              </p>
            ) : (
              <div className="flex gap-6 flex-wrap">
                {orgs.map((org) => {
                  const available = ugByOrgId[org.id];
                  return (
                    <OrgColumn
                      key={org.membershipId}
                      accountId={accountId}
                      org={org}
                      usergroups={available?.usergroups ?? []}
                      onOrgRemoved={() => {
                        queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'organisations'] });
                      }}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
